import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  AfterContentInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  HostListener,
  ElementRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { SegmentedItemComponent } from './segmented-item.component';

/**
 * Segmented Control Component
 *
 * A pill-shaped toggle group for switching between mutually exclusive views or modes.
 * Matches Figma Navigation/Segmented (node 5544-27044).
 *
 * Figma property → Angular mapping:
 *   - Property 1 ("Segmented") → component selector (single visual style, no variant input)
 *   - Item children             → projected <hc-segmented-item> components
 *
 * Figma specs (container):
 *   - Background:    --hc-semantic-background-general-primary (white)
 *   - Border:        1px solid --hc-semantic-border-general-divider (#d1d5db)
 *   - Border-radius: --hc-border-radius-full (9999px)
 *   - Padding:       --hc-spacing-xs (0.25rem / 4px)
 *   - Gap:           --hc-spacing-xs (0.25rem / 4px)
 *   - Items:         flex: 1 0 0 (equal width distribution)
 *
 * Figma specs (item — idle):
 *   - Background:    --hc-semantic-background-general-primary (white)
 *   - Text color:    --hc-semantic-content-action-secondary-idle (#1f2937)
 *   - Padding:       0.625rem 1.5rem (10px 24px)
 *   - Border-radius: --hc-border-radius-full (9999px)
 *   - Gap:           --hc-spacing-sm (0.5rem / 8px)
 *   - Typography:    label/medium (16px, weight 600, line-height 24px)
 *   - Icon:          24x24px
 *
 * Figma specs (item — active/selected):
 *   - Background:    --hc-semantic-background-general-secondary (#e5e7eb)
 *   - Text color:    --hc-semantic-content-general-secondary (#4b5563)
 *
 * Note: Figma defines only idle and active states. Hover, focus, and disabled
 * states are derived from the design system's interaction patterns:
 *   - Hover:    --hc-semantic-background-general-secondary-light (#f3f4f6)
 *   - Focus:    2px outline --hc-semantic-misc-focus (#3474da)
 *   - Disabled: --hc-semantic-content-action-disabled-idle (#6b7280)
 *
 * @example
 * ```html
 * <hc-segmented [(value)]="selectedView">
 *   <hc-segmented-item value="room">Per room</hc-segmented-item>
 *   <hc-segmented-item value="overview">Overview</hc-segmented-item>
 * </hc-segmented>
 *
 * <!-- Many or long options: let them wrap onto extra rows on narrow screens -->
 * <hc-segmented [(value)]="mealOption" [wrap]="true">...</hc-segmented>
 * ```
 */
@Component({
  selector: 'hc-segmented',
  standalone: false,
  templateUrl: './segmented.component.html',
  styleUrl: './segmented.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SegmentedComponent implements AfterContentInit, OnChanges, OnDestroy {
  /** Currently selected segment value (two-way bindable) */
  @Input() value = '';

  /** Disables all segments */
  @Input() disabled = false;

  /**
   * Lets segments wrap onto extra rows when they don't fit the available width.
   * Off by default so existing single-row pill controls keep their look.
   */
  @Input() wrap = false;

  /** Emits when the selected segment changes */
  @Output() valueChange = new EventEmitter<string>();

  @ContentChildren(SegmentedItemComponent) items!: QueryList<SegmentedItemComponent>;

  private itemsChangeSub?: Subscription;

  constructor(private elementRef: ElementRef) {}

  ngAfterContentInit(): void {
    this.syncItems();
    this.itemsChangeSub = this.items.changes.subscribe(() => this.syncItems());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['value'] || changes['disabled']) && this.items) {
      this.syncItems();
    }
  }

  ngOnDestroy(): void {
    this.itemsChangeSub?.unsubscribe();
  }

  /** Handle clicks on child items via event delegation */
  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    const clickedItem = this.findItemFromEvent(event);
    if (!clickedItem || clickedItem.isDisabled || clickedItem.isActive) return;
    this.selectItem(clickedItem);
  }

  /** Keyboard navigation: arrow keys move between segments */
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const enabledItems = this.getEnabledItems();
    if (enabledItems.length === 0) return;

    const currentIndex = enabledItems.findIndex(item => item.isActive);
    let targetIndex: number;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = currentIndex < enabledItems.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = currentIndex > 0 ? currentIndex - 1 : enabledItems.length - 1;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = enabledItems.length - 1;
        break;
      default:
        return;
    }

    this.selectItem(enabledItems[targetIndex]);
  }

  private selectItem(item: SegmentedItemComponent): void {
    this.value = item.value;
    this.valueChange.emit(this.value);
    this.syncItems();
    this.focusItem(item);
  }

  private syncItems(): void {
    this.items?.forEach(item => {
      item.isActive = item.value === this.value;
      item.groupDisabled = this.disabled;
      item.cdr.markForCheck();
    });
  }

  private getEnabledItems(): SegmentedItemComponent[] {
    return this.items?.toArray().filter(item => !item.isDisabled) ?? [];
  }

  private findItemFromEvent(event: Event): SegmentedItemComponent | null {
    const target = event.target as HTMLElement;
    const button = target.closest('.hc-segmented__item') as HTMLElement;
    if (!button) return null;

    const allButtons = Array.from(
      this.elementRef.nativeElement.querySelectorAll('.hc-segmented__item')
    );
    const index = allButtons.indexOf(button);
    return index >= 0 ? this.items.toArray()[index] : null;
  }

  private focusItem(item: SegmentedItemComponent): void {
    const itemsArray = this.items.toArray();
    const index = itemsArray.indexOf(item);
    const buttons = this.elementRef.nativeElement.querySelectorAll('.hc-segmented__item');
    (buttons[index] as HTMLElement)?.focus();
  }
}
